import { useMemo } from "react";
import { Loader2 } from "lucide-react";
import type {
  DecisionDefinition,
  DecisionEditorState,
} from "@/types/decision-definition-schema";
import { extractCaseContextFields } from "@/types/decision-editor";
import type { CaseContextField } from "@/types/decision-editor";
import { DecisionGraph } from "@/components/decision-graph/DecisionGraph";

// =============================================================================
// Props
// =============================================================================

export interface DecisionEditorProps {
  definition: DecisionDefinition;
  editorState?: DecisionEditorState;
  onChange: (
    definition: DecisionDefinition,
    editorState?: DecisionEditorState
  ) => void;
  readOnly?: boolean;
  isLoading?: boolean;
  inputSchema?: CaseContextField[];
}

// =============================================================================
// Component — thin wrapper around the full-canvas DecisionGraph
// =============================================================================

export function DecisionEditor({
  definition,
  editorState,
  onChange,
  readOnly = false,
  isLoading = false,
  inputSchema,
}: DecisionEditorProps) {
  const caseContextFields = useMemo(() => {
    if (inputSchema && inputSchema.length > 0) return inputSchema;
    return extractCaseContextFields(definition);
  }, [definition, inputSchema]);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 w-full flex-1">
      <DecisionGraph
        definition={definition}
        editorState={editorState}
        onChange={onChange}
        readOnly={readOnly}
        caseContextSchema={caseContextFields}
        decisionId={definition.metadata?.id}
      />
    </div>
  );
}
